import { useEffect, useRef } from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { CreditCard, Shield, Zap, Smartphone, Monitor, Wifi, ArrowRight, CheckCircle2, Users, BarChart3, Settings, Globe, Package, Clock } from "lucide-react";
import { TERMINALS_DATA } from "@/data/terminals";
import { useTranslation } from "@/i18n/translations";

function A({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const v = useInView(ref, { once: true, margin: "-60px" });
  return <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={v ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>{children}</motion.div>;
}

const TERMINAL_LIST = ["ams1", "sfo1", "nyc1", "s1f2", "p630", "m450", "s1e2l"].map(slug => TERMINALS_DATA[slug]);

export default function TerminalsInfoPage() {
  const { t: tr } = useTranslation();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const CATEGORIES = [
    { icon: Smartphone, title: tr("tinfo.mobile"), desc: tr("tinfo.mobileD"), terminals: ["AMS1", "NYC1", "S1F2", "S1E2L"] },
    { icon: Monitor, title: tr("tinfo.counter"), desc: tr("tinfo.counterD"), terminals: ["SFO1", "P630", "M450"] },
    { icon: CreditCard, title: tr("tinfo.tap"), desc: tr("tinfo.tapD"), terminals: ["Compatible Android & iPhone"] },
  ];

  const ADVANTAGES = [
    { icon: Package, title: tr("tinfo.free"), desc: tr("tinfo.freeD") },
    { icon: Shield, title: tr("tinfo.insure"), desc: tr("tinfo.insureD") },
    { icon: Wifi, title: tr("tinfo.sim"), desc: tr("tinfo.simD") },
    { icon: Clock, title: tr("tinfo.install"), desc: tr("tinfo.installD") },
    { icon: Settings, title: tr("tinfo.manage"), desc: tr("tinfo.manageD") },
    { icon: Zap, title: tr("tinfo.offline"), desc: tr("tinfo.offlineD") },
  ];

  const ADV_FEATURES = [
    { title: tr("tinfo.offlineF"), desc: tr("tinfo.offlineFD") },
    { title: tr("tinfo.payTable"), desc: tr("tinfo.payTableD") },
    { title: tr("tinfo.tips"), desc: tr("tinfo.tipsD") },
    { title: tr("tinfo.cashback"), desc: tr("tinfo.cashbackD") },
    { title: tr("tinfo.branding"), desc: tr("tinfo.brandingD") },
    { title: tr("tinfo.pin"), desc: tr("tinfo.pinD") },
  ];

  const STORE_BENEFITS = [
    { icon: Users, title: tr("tinfo.queue"), desc: tr("tinfo.queueD") },
    { icon: BarChart3, title: tr("tinfo.data"), desc: tr("tinfo.dataD") },
    { icon: Globe, title: tr("tinfo.local"), desc: tr("tinfo.localD") },
    { icon: CreditCard, title: tr("tinfo.loyalty"), desc: tr("tinfo.loyaltyD") },
  ];

  return (
    <div data-testid="terminals-info-page">
      <SEO page="tinfo" />
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[#0A1128]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <A>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("tinfo.tag")}</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mt-4 leading-[1.1]">{tr("tinfo.title")}</h1>
              <p className="text-base md:text-lg text-slate-300 mt-6 leading-relaxed">{tr("tinfo.desc")}</p>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 font-semibold bg-[#00C853] text-white rounded-full px-7 py-3 text-sm hover:bg-[#00A844] transition-all">{tr("tinfo.demo")} <ArrowRight size={16} /></Link>
                <Link to="/tarifs" className="inline-flex items-center justify-center font-semibold border-2 border-white/20 text-white rounded-full px-7 py-3 text-sm hover:bg-white/10 transition-all">{tr("tinfo.offer")}</Link>
              </div>
            </A>
            <A delay={0.2}>
              <img src="https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL0Q1VXFzWG83ZnR6TWE3ZGc2TG9DLmpwZyJ9:adyen:EvUNprVyhPrmx12zzMniSLhHxnl7m5qcQtn13On7KsM?format=webp&width=800&height=1000" alt={tr("tinfo.title")} loading="lazy" className="w-full rounded-2xl border border-white/10 max-h-[500px] object-cover" />
            </A>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <A className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("tinfo.advTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{tr("tinfo.advTitle")}</h2>
          </A>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {ADVANTAGES.map((f, i) => (
              <A key={i} delay={i * 0.08}>
                <div className="feature-card bg-[#F8F9FA] rounded-xl border border-slate-100 p-8 h-full">
                  <div className="w-11 h-11 rounded-lg bg-[#00C853]/10 flex items-center justify-center mb-5"><f.icon size={22} className="text-[#00C853]" /></div>
                  <h3 className="text-lg font-semibold text-[#0A1128] mb-2">{f.title}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed">{f.desc}</p>
                </div>
              </A>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <A className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("tinfo.catTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{tr("tinfo.catTitle")}</h2>
          </A>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CATEGORIES.map((cat, i) => (
              <A key={i} delay={i * 0.1}>
                <div className="bg-white rounded-xl border border-slate-200 p-8 h-full">
                  <div className="w-11 h-11 rounded-lg bg-[#00C853]/10 flex items-center justify-center mb-5"><cat.icon size={22} className="text-[#00C853]" /></div>
                  <h3 className="text-lg font-semibold text-[#0A1128] mb-2">{cat.title}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed mb-4">{cat.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {cat.terminals.map((tname, ti) => (
                      <span key={ti} className="text-xs font-medium text-[#1B2B5B] bg-[#1B2B5B]/5 rounded-full px-3 py-1">{tname}</span>
                    ))}
                  </div>
                </div>
              </A>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <A className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("tinfo.rangeTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{tr("tinfo.rangeTitle")}</h2>
          </A>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {TERMINAL_LIST.map((term, i) => (
              <A key={i} delay={i * 0.06}>
                <Link to={`/terminaux/${term.slug}`} className="block">
                  <div className="feature-card bg-[#F8F9FA] rounded-xl border border-slate-100 p-6 text-center h-full">
                    <img src={term.image} alt={term.name} loading="lazy" className="h-40 lg:h-48 object-contain mx-auto mb-4" />
                    <span className="text-xs font-medium text-[#00C853]">{term.category}</span>
                    <h3 className="text-base font-semibold text-[#0A1128] mt-1">{term.name}</h3>
                    <p className="text-xs text-[#475569] mt-1">{term.tagline}</p>
                  </div>
                </Link>
              </A>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <A>
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{tr("tinfo.compTag")}</span>
              <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{tr("tinfo.compTitle")}</h2>
              <p className="text-base text-[#475569] mt-4 leading-relaxed">{tr("tinfo.compDesc")}</p>
              <div className="grid grid-cols-2 gap-3 mt-6">
                {["Visa & Mastercard", "American Express", "Twint", "Apple Pay", "Google Pay", "Samsung Pay", "PostFinance", "Maestro", "NFC", "QR codes", "Gift cards", "DCC"].map((m, i) => (
                  <div key={i} className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#00C853] shrink-0" /><span className="text-sm text-[#475569]">{m}</span></div>
                ))}
              </div>
            </A>
            <A delay={0.2}>
              <div className="bg-[#0A1128] rounded-2xl p-8">
                <h3 className="text-xl font-bold text-white mb-5">{tr("tinfo.advFeat")}</h3>
                {ADV_FEATURES.map((o, i) => (
                  <div key={i} className="flex items-start gap-3 mt-3 first:mt-0">
                    <CheckCircle2 size={14} className="text-[#00C853] shrink-0 mt-0.5" />
                    <div><div className="text-sm font-medium text-white">{o.title}</div><div className="text-xs text-slate-400">{o.desc}</div></div>
                  </div>
                ))}
              </div>
            </A>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <A className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight">{tr("tinfo.storeTitle")}</h2>
          </A>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STORE_BENEFITS.map((s, i) => (
              <A key={i} delay={i * 0.08}>
                <div className="bg-[#F8F9FA] rounded-xl border border-slate-100 p-6 text-center h-full">
                  <div className="w-10 h-10 rounded-lg bg-[#00C853]/10 flex items-center justify-center mx-auto mb-4"><s.icon size={18} className="text-[#00C853]" /></div>
                  <h3 className="text-sm font-semibold text-[#0A1128] mb-1">{s.title}</h3>
                  <p className="text-xs text-[#475569]">{s.desc}</p>
                </div>
              </A>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#1B2B5B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <A>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">{tr("tinfo.ctaTitle")}</h2>
            <p className="text-base text-slate-300 mt-4 max-w-md mx-auto">{tr("tinfo.ctaDesc")}</p>
            <Link to="/contact" className="inline-flex items-center gap-2 mt-8 font-semibold bg-[#00C853] text-white rounded-full px-8 py-3.5 text-sm hover:bg-[#00A844] transition-all">{tr("tinfo.demo")} <ArrowRight size={16} /></Link>
          </A>
        </div>
      </section>
    </div>
  );
}
